<div
  class="agent-chat"
  [class.standalone-mode]="!showClose"
  [class.popup-mode]="showClose"
  *ngIf="agent as a"
  [attr.data-testid]="'ai-agents-chat-' + a.key"
>
  <header class="chat-header">
    <div class="header-title">
      <i class="dx-icon dx-icon-{{ a.icon }}"></i>
      <div>
        <div class="title-main">{{ headerName }}</div>
        <div class="title-sub" *ngIf="!displayName">{{ a.description }}</div>
      </div>
    </div>
    <div class="header-actions">
      <dx-button
        *ngIf="showEscalate"
        [text]="escalating ? 'Summarizing…' : 'Talk to a human'"
        icon="user"
        stylingMode="outlined"
        [disabled]="!canEscalate || escalating"
        hint="Hand this conversation to a Medigi support agent"
        [elementAttr]="{ 'data-testid': 'ai-agents-escalate-btn-' + a.key }"
        (onClick)="escalateToHuman()"
      ></dx-button>
      <dx-button
        *ngIf="showExpand"
        icon="expand"
        stylingMode="text"
        hint="Open in fullscreen"
        [elementAttr]="{ 'data-testid': 'ai-agents-expand-btn-' + a.key }"
        (onClick)="expandToFullscreen()"
      ></dx-button>
      <dx-button
        *ngIf="isAdmin"
        icon="bulletlist"
        text="Manage KB"
        stylingMode="outlined"
        hint="Open the knowledge-base manager"
        [elementAttr]="{ 'data-testid': 'ai-agents-manage-kb-btn-' + a.key }"
        (onClick)="openKbManager()"
      ></dx-button>
      <dx-button
        text="New chat"
        icon="refresh"
        stylingMode="outlined"
        hint="Start a fresh session — clears the conversation and resets the agent's memory"
        [elementAttr]="{ 'data-testid': 'ai-agents-new-chat-btn-' + a.key }"
        (onClick)="newChat()"
      ></dx-button>
      <dx-button
        *ngIf="showClose"
        icon="close"
        stylingMode="text"
        hint="Close"
        [elementAttr]="{ 'data-testid': 'ai-agents-close-btn-' + a.key }"
        (onClick)="close.emit()"
      ></dx-button>
    </div>
  </header>

  <div
    class="trimmed-notice"
    *ngIf="trimmedThisSession"
    [attr.data-testid]="'ai-agents-trimmed-notice-' + a.key"
  >
    Older messages trimmed to keep this chat snappy (cap: 20).
  </div>

  <div class="chat-scroll" [class.scroll-empty]="messages.length === 0">
    <!-- Screen 1: Empty State (Google Gemini Landing mimic) -->
    <div class="chat-empty-container" *ngIf="messages.length === 0">
      <div class="welcome-avatar-container">
        <img class="welcome-avatar" src="assets/img/ai/medas-hello.png" alt="Dr. Medas Hello" />
      </div>
      <div class="empty-welcome">
        <h1 class="welcome-title">Meet {{ a.name }}, your personal AI assistant</h1>
      </div>
      
      <!-- Suggestion pills -->
      <div class="suggestion-pills">
        <button class="pill-btn" (click)="selectSuggestion('I need support with an issue')">
          <i class="dx-icon dx-icon-tel"></i> Support
        </button>
        <button class="pill-btn" (click)="selectSuggestion('Analyze my orders and stats')">
          <i class="dx-icon dx-icon-chart"></i> Analytics
        </button>
        <button class="pill-btn" (click)="selectSuggestion('How do I purchase a single item?')">
          <i class="dx-icon dx-icon-card"></i> Purchases
        </button>
        <button class="pill-btn" (click)="selectSuggestion('How do I use this app?')">
          <i class="dx-icon dx-icon-help"></i> How to use
        </button>
      </div>
    </div>

    <!-- Screen 2: Active Session (Google Gemini Active Session mimic) -->
    <div
      *ngFor="let m of messages; let i = index; trackBy: trackByIndex"
      class="msg-row"
      [class.msg-user]="m.role === 'user'"
      [class.msg-agent]="m.role === 'agent'"
      [attr.data-testid]="'ai-agents-message-' + a.key + '-' + i"
    >
      <!-- Dynamic Dr. Medas avatar on the left for agent messages -->
      <div class="msg-avatar image-avatar" *ngIf="m.role === 'agent'">
        <img [src]="getAgentAvatar(m)" class="avatar-img" [alt]="a.name" />
      </div>

      <div class="msg-content-wrapper">
        <div
          class="msg-bubble"
          [class.has-chart]="m.chart"
          [class.has-table]="m.role === 'agent' && hasTable(m.text)"
        >
          <ng-container *ngIf="m.role === 'user'">
            <div class="msg-text" *ngIf="m.text">{{ m.text }}</div>
            <div class="msg-attachments" *ngIf="m.attachments?.length">
              <a
                *ngFor="let att of m.attachments"
                class="attachment-chip readonly"
                [class.chip-state-license]="att.license_type === 'state_license'"
                [class.chip-dea-license]="att.license_type === 'dea_license'"
                [href]="att.url"
                target="_blank"
                rel="noopener noreferrer"
                [title]="att.filename"
              >
                <i class="dx-icon dx-icon-doc"></i>
                <span class="chip-kind" *ngIf="att.license_type === 'state_license'">State&nbsp;License</span>
                <span class="chip-kind" *ngIf="att.license_type === 'dea_license'">DEA&nbsp;License</span>
                <span class="chip-filename">{{ att.filename }}</span>
              </a>
            </div>
          </ng-container>

          <ng-container *ngIf="m.role === 'agent'">
            <!-- Multi-section async report (V2 report pipeline) -->
            <ng-container *ngIf="m.report as rep">
              <div class="report" [attr.data-testid]="'ai-agents-report-' + a.key + '-' + i">
                <div class="report-statusbar">
                  <dx-load-indicator *ngIf="m.pending" [height]="14" [width]="14"></dx-load-indicator>
                  <span class="report-status-label">{{ reportStatusLabel(rep) }}</span>
                  <span class="report-progress" *ngIf="rep.total">{{ rep.completed }}/{{ rep.total }}</span>
                  <!-- Export the finished report as Markdown (feed to another AI). -->
                  <dx-button
                    *ngIf="rep.status === 'done' && rep.sections.length"
                    class="report-download-btn"
                    stylingMode="text"
                    icon="download"
                    hint="Download this report as Markdown"
                    text="Download .md"
                    (onClick)="downloadReportAsMarkdown(rep, messages[i - 1]?.text)"
                    [elementAttr]="{ 'data-testid': 'ai-agents-report-download-md-btn-' + a.key + '-' + i }"
                  ></dx-button>
                  <!-- Regenerate this report (re-asks the original request). -->
                  <dx-button
                    *ngIf="rep.status === 'done'"
                    stylingMode="text"
                    icon="refresh"
                    hint="Regenerate this report"
                    text="Re-run"
                    [disabled]="sending"
                    (onClick)="rerunReport(rep, messages[i - 1]?.text)"
                    [elementAttr]="{ 'data-testid': 'ai-agents-report-rerun-btn-' + a.key + '-' + i }"
                  ></dx-button>
                </div>

                <div class="report-plan" *ngIf="rep.plan?.length && rep.sections.length === 0">
                  <div class="plan-step" *ngFor="let p of rep.plan">• {{ p.title }}</div>
                </div>

                <div class="report-section" *ngFor="let sec of rep.sections">
                  <h4 class="report-section-title" *ngIf="sec.title">{{ sec.title }}</h4>

                  <div class="kpi-grid" *ngIf="sec.type === 'kpi'">
                    <div class="kpi-tile" *ngFor="let it of sec.data?.items">
                      <span class="kpi-label">{{ it.label }}</span>
                      <span class="kpi-value">{{ it.value === null || it.value === undefined ? '—' : it.value }}</span>
                    </div>
                  </div>

                  <dx-chart
                    *ngIf="sec.type === 'chart' && sec.data && sec.data.chartType !== 'pie'"
                    class="msg-chart"
                    [dataSource]="sec.data.rows"
                  >
                    <dxo-common-series-settings
                      [argumentField]="sec.data.argumentField"
                      [type]="sec.data.chartType"
                    ></dxo-common-series-settings>
                    <dxi-series
                      *ngFor="let f of sec.data.valueFields"
                      [valueField]="f"
                      [name]="f"
                    ></dxi-series>
                    <dxi-value-axis>
                      <dxo-label [format]="sec.data.valueFormat"></dxo-label>
                    </dxi-value-axis>
                    <dxo-legend [visible]="sec.data.valueFields.length > 1"></dxo-legend>
                    <dxo-tooltip
                      [enabled]="true"
                      [format]="sec.data.valueFormat"
                      [customizeTooltip]="reportChartTooltip"
                    ></dxo-tooltip>
                    <dxo-export [enabled]="true"></dxo-export>
                  </dx-chart>

                  <dx-pie-chart
                    *ngIf="sec.type === 'chart' && sec.data?.chartType === 'pie'"
                    class="msg-chart"
                    [dataSource]="sec.data.rows"
                  >
                    <dxi-series
                      [argumentField]="sec.data.argumentField"
                      [valueField]="sec.data.valueFields[0]"
                    >
                      <!-- Percentage label only on slices >10% (big slices),
                           with a connector line; small slices stay blank to
                           avoid clutter. Full name + value + % is in the hover
                           tooltip. Legend stays off (doesn't scale). -->
                      <dxo-label
                        [visible]="true"
                        [customizeText]="piePercentLabel"
                        backgroundColor="none"
                      >
                        <dxo-connector [visible]="true" [width]="1"></dxo-connector>
                      </dxo-label>
                    </dxi-series>
                    <dxo-legend [visible]="false"></dxo-legend>
                    <dxo-tooltip
                      [enabled]="true"
                      [format]="sec.data.valueFormat"
                      [customizeTooltip]="reportChartTooltip"
                    ></dxo-tooltip>
                    <dxo-export [enabled]="true"></dxo-export>
                  </dx-pie-chart>

                  <dx-data-grid
                    *ngIf="sec.type === 'grid'"
                    class="report-grid"
                    [dataSource]="sec.data?.rows"
                    [showBorders]="true"
                    [columnAutoWidth]="true"
                    [allowColumnResizing]="true"
                    [rowAlternationEnabled]="true"
                  >
                    <dxi-column
                      *ngFor="let col of sec.data?.columns"
                      [dataField]="col.field"
                      [caption]="col.caption"
                      [format]="col.format"
                    ></dxi-column>
                    <dxo-paging [pageSize]="10"></dxo-paging>
                    <dxo-export [enabled]="true"></dxo-export>
                  </dx-data-grid>

                  <markdown
                    *ngIf="sec.type === 'text'"
                    class="msg-text msg-md"
                    [data]="sec.narrative"
                  ></markdown>

                  <!-- Provenance: how this section was built + why it was picked.
                       Always visible — internal ops wants to verify the numbers. -->
                  <div class="section-method" *ngIf="sec.method">
                    Built from {{ sec.method.how }}<span *ngIf="sec.method.why">&nbsp;{{ sec.method.why }}</span>
                  </div>
                </div>

                <!-- Suggested follow-up chips — only on a finished report, not
                     while still streaming. Each submits a NEW report. -->
                <div
                  class="report-followups"
                  *ngIf="rep.status === 'done' && rep.followups?.length"
                  [attr.data-testid]="'ai-agents-report-followups-' + a.key + '-' + i"
                >
                  <span class="followups-heading">Ask a follow-up</span>
                  <div class="followups-chips">
                    <button
                      type="button"
                      class="followup-chip"
                      *ngFor="let q of rep.followups; let fi = index"
                      [disabled]="sending"
                      (click)="askFollowup(q, rep, messages[i - 1]?.text)"
                      [attr.data-testid]="'ai-agents-report-followup-chip-' + a.key + '-' + i + '-' + fi"
                    >
                      {{ q }}
                    </button>
                  </div>
                </div>

                <div class="msg-error" *ngIf="rep.error">{{ rep.error }}</div>
              </div>
            </ng-container>

            <ng-container *ngIf="!m.report && m.pending">
              <div class="msg-pending">
                <dx-load-indicator [height]="18" [width]="18"></dx-load-indicator>
                <span>Agent is thinking…</span>
              </div>
              <div class="msg-progress" *ngIf="m.progress?.length">
                <div class="progress-step" *ngFor="let step of m.progress">{{ step }}</div>
              </div>
            </ng-container>

            <ng-container *ngIf="!m.report && !m.pending && m.error">
              <div
                class="msg-error"
                [attr.data-testid]="'ai-agents-error-' + a.key"
              >
                <div class="error-title">
                  {{ m.error }}
                </div>
                <div class="error-actions">
                  <dx-button
                    *ngIf="!m.errorStatus || m.errorStatus === 0 || m.errorStatus >= 500"
                    text="Retry"
                    icon="refresh"
                    stylingMode="text"
                    [elementAttr]="{ 'data-testid': 'ai-agents-retry-btn-' + a.key }"
                    (onClick)="retry(m)"
                  ></dx-button>
                  <dx-button
                    *ngIf="m.errorStatus && m.errorStatus >= 400 && m.errorStatus < 500"
                    text="Copy details"
                    icon="copy"
                    stylingMode="text"
                    (onClick)="copyErrorDetails(m)"
                  ></dx-button>
                </div>
              </div>
            </ng-container>

            <ng-container *ngIf="!m.report && !m.pending && !m.error">
              <div class="persona-trail" *ngIf="m.personas?.length">
                <span class="persona-badge" *ngFor="let p of m.personas">{{ p }}</span>
              </div>
              <div class="side-effects" *ngIf="m.sideEffects?.length">
                <span
                  *ngFor="let se of m.sideEffects; let sei = index"
                  class="side-effect-chip"
                  [attr.data-testid]="'ai-agents-side-effect-' + a.key + '-' + sei"
                >
                  <i class="dx-icon dx-icon-check"></i>
                  {{ se.detail }}
                </span>
              </div>
              <markdown
                class="msg-text msg-md"
                [data]="m.text"
                [disableSanitizer]="false"
              ></markdown>

              <ng-container *ngIf="m.chart as c">
                <dx-chart
                  *ngIf="c.chartType !== 'pie'"
                  class="msg-chart"
                  [dataSource]="c.data"
                  [title]="c.title"
                >
                  <dxo-common-series-settings
                    [argumentField]="c.argumentField"
                    [type]="c.chartType"
                  ></dxo-common-series-settings>
                  <dxi-series
                    *ngFor="let f of c.valueFields"
                    [valueField]="f"
                    [name]="f"
                  ></dxi-series>
                  <dxo-legend [visible]="c.valueFields.length > 1"></dxo-legend>
                  <dxo-tooltip [enabled]="true"></dxo-tooltip>
                  <dxo-export [enabled]="true"></dxo-export>
                </dx-chart>

                <dx-pie-chart
                  *ngIf="c.chartType === 'pie'"
                  class="msg-chart"
                  [dataSource]="c.data"
                  [title]="c.title"
                >
                  <dxi-series [argumentField]="c.argumentField" [valueField]="c.valueFields[0]">
                    <dxo-label [visible]="true">
                      <dxo-connector [visible]="true"></dxo-connector>
                    </dxo-label>
                  </dxi-series>
                  <dxo-legend [visible]="true"></dxo-legend>
                  <dxo-tooltip [enabled]="true"></dxo-tooltip>
                  <dxo-export [enabled]="true"></dxo-export>
                </dx-pie-chart>
              </ng-container>

              <div class="msg-sources" *ngIf="m.sources && m.sources.length > 0">
                <span class="sources-label">Sources:</span>
                <a
                  *ngFor="let s of m.sources; let si = index"
                  class="source-chip"
                  [href]="s.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  [title]="s.snippet"
                  [attr.data-testid]="'ai-agents-source-' + a.key + '-' + si"
                >
                  [{{ si + 1 }}] {{ s.title }}
                </a>
              </div>
            </ng-container>
          </ng-container>
        </div>

        <!-- Action bar under completed agent messages -->
        <div class="msg-actions-row" *ngIf="m.role === 'agent' && !m.pending && !m.error">
          <dx-button
            icon="refresh"
            stylingMode="text"
            hint="Regenerate"
            class="action-btn"
            (onClick)="retry(m)"
          ></dx-button>
          <dx-button
            icon="copy"
            stylingMode="text"
            hint="Copy text"
            class="action-btn"
            (onClick)="copyToClipboard(m.text)"
          ></dx-button>
        </div>
      </div>

      <!-- User dynamic initials avatar on the right for user messages -->
      <div class="msg-avatar user-avatar" *ngIf="m.role === 'user'" [title]="userName">
        <span class="avatar-initials">{{ userInitials }}</span>
      </div>
    </div>
  </div>

  <!-- Sleek, Pill-Shaped Input Area centered like Gemini -->
  <footer class="chat-input-bar">
    <!-- Hidden file inputs for the two license-upload buttons -->
    <input
      #stateLicenseInput
      type="file"
      hidden
      accept="application/pdf,image/png,image/jpeg,image/jpg,image/heic"
      [attr.data-testid]="'ai-agents-state-license-input-' + a.key"
      (change)="onLicenseFilePicked($event, 'state_license')"
    />
    <input
      #deaLicenseInput
      type="file"
      hidden
      accept="application/pdf,image/png,image/jpeg,image/jpg,image/heic"
      [attr.data-testid]="'ai-agents-dea-license-input-' + a.key"
      (change)="onLicenseFilePicked($event, 'dea_license')"
    />

    <div class="input-container-pill shadow-sm">
      <!-- Attachment chips for licenses staged this turn -->
      <div class="attachment-strip" *ngIf="pendingAttachments.length > 0 || uploadError">
        <div
          *ngFor="let att of pendingAttachments; let i = index"
          class="attachment-chip"
          [class.chip-state-license]="att.license_type === 'state_license'"
          [class.chip-dea-license]="att.license_type === 'dea_license'"
          [attr.data-testid]="'ai-agents-attachment-chip-' + a.key + '-' + i"
        >
          <i class="dx-icon dx-icon-doc"></i>
          <span class="chip-kind" *ngIf="att.license_type === 'state_license'">State&nbsp;License</span>
          <span class="chip-kind" *ngIf="att.license_type === 'dea_license'">DEA&nbsp;License</span>
          <span class="chip-filename">{{ att.filename }}</span>
          <button
            type="button"
            class="chip-remove"
            (click)="removeAttachment(i)"
            [attr.data-testid]="'ai-agents-attachment-remove-' + a.key + '-' + i"
          >×</button>
        </div>
        <div class="attachment-error" *ngIf="uploadError">{{ uploadError }}</div>
      </div>

      <dx-text-area
        [(value)]="draft"
        valueChangeEvent="input"
        [height]="54"
        placeholder="Ask {{ headerName }}…"
        [disabled]="sending"
        [inputAttr]="{ 'data-testid': 'ai-agents-input-' + a.key }"
        (onKeyDown)="onInputKeydown($any($event.event))"
      ></dx-text-area>

      <div class="input-actions-row">
        <div class="left-actions">
          <dx-button
            *ngIf="isBuyer"
            icon="file"
            text="State License"
            stylingMode="text"
            hint="Upload a state license PDF — agent will validate and update / register the facility"
            class="icon-btn-pill license-btn"
            [disabled]="sending || uploadingLicenseType !== null"
            [elementAttr]="{ 'data-testid': 'ai-agents-upload-state-license-btn-' + a.key }"
            (onClick)="triggerLicenseUpload('state_license', stateLicenseInput)"
          ></dx-button>
          <dx-button
            *ngIf="isBuyer"
            icon="file"
            text="DEA License"
            stylingMode="text"
            hint="Upload a DEA license PDF — agent will validate and update / register the facility"
            class="icon-btn-pill license-btn"
            [disabled]="sending || uploadingLicenseType !== null"
            [elementAttr]="{ 'data-testid': 'ai-agents-upload-dea-license-btn-' + a.key }"
            (onClick)="triggerLicenseUpload('dea_license', deaLicenseInput)"
          ></dx-button>
          <span class="upload-spinner" *ngIf="isBuyer && uploadingLicenseType">
            <dx-load-indicator [height]="14" [width]="14"></dx-load-indicator>
            Uploading…
          </span>
          <dx-button
            icon="download"
            stylingMode="text"
            hint="Download conversation as JSON"
            class="icon-btn-pill"
            [disabled]="messages.length === 0"
            [elementAttr]="{ 'data-testid': 'ai-agents-download-conversation-btn-' + a.key }"
            (onClick)="downloadConversationAsJson()"
          ></dx-button>
          <dx-button
            *ngIf="isAdmin"
            icon="chart"
            text="Report"
            stylingMode="text"
            hint="Build a multi-section company report from your question"
            class="icon-btn-pill report-btn"
            [disabled]="!canReport"
            [elementAttr]="{ 'data-testid': 'ai-agents-report-btn-' + a.key }"
            (onClick)="runReport()"
          ></dx-button>
        </div>
        
        <div class="right-actions">
          <div class="model-selector" title="AI Model">
            <span class="model-dot"></span>
            <span class="model-name">Medigi AI</span>
            <i class="dx-icon dx-icon-chevron-down"></i>
          </div>
          <dx-button
            icon="arrowright"
            type="default"
            stylingMode="contained"
            class="send-pill-btn"
            [disabled]="!canSend"
            [elementAttr]="{ 'data-testid': 'ai-agents-send-btn-' + a.key }"
            (onClick)="send()"
          ></dx-button>
        </div>
      </div>
    </div>
    
    <div class="input-disclaimer">
      {{ headerName }} is AI and can make mistakes.
    </div>
  </footer>
</div>
